// Demo input loading and validation.
//
// Reads a JSON file from disk, validates the shape, and returns a
// strongly-typed DemoFile. The validation layer lives in
// `./validate.ts` and is independent of the calculation engine.
//
// Top-level shapes supported:
//   - `new-supply`         — single new-supply calculation
//   - `change-premium`     — single change-premium calculation
//   - `excess-gfa`         — single excess-GFA (× 1.3) calculation
//   - `temporary-use`      — single temporary-use fee calculation
//   - `short-term-lease`   — single short-term lease (alias of
//                            temporary-use with the §5 末段 flow)
//   - `lease-rent`         — single key industrial project lease
//                            annual rent (20-year × 3%) calculation
//   - `capacity-increase`  — single change-premium with the M1/M0/
//                            W1/W0 scaling layered on top
//   - `batch`              — multiple cases (each may be new-supply
//                            or change-premium), validated
//                            independently

import type { ChangePremiumInput } from "../types/change-premium.js";
import type { BuildingItem } from "../types/building-item.js";
import type { MarketPriceSet } from "../types/market-price.js";
import type { Parcel } from "../types/parcel.js";
import type { ValidationMessage } from "../types/validation.js";

import { validateDemoFile } from "./validate.js";

export type DemoCalculationType =
  | "new-supply"
  | "change-premium"
  | "excess-gfa"
  | "temporary-use"
  | "short-term-lease"
  | "lease-rent"
  | "capacity-increase"
  | "batch";

export interface DemoNewSupplyFile {
  readonly calculationType: "new-supply";
  readonly input: {
    readonly parcel: Parcel;
    readonly prices: MarketPriceSet;
    readonly items: BuildingItem[];
  };
}

export interface DemoChangePremiumFile {
  readonly calculationType: "change-premium";
  readonly input: ChangePremiumInput;
}

/**
 * Excess-GFA (竣工超面积) demo input. Identical to the
 * change-premium shape plus a `planningConfirmed` flag.
 */
export interface DemoExcessGfaFile {
  readonly calculationType: "excess-gfa";
  readonly input: ChangePremiumInput & { readonly planningConfirmed: boolean };
}

export interface DemoTemporaryUseFile {
  readonly calculationType: "temporary-use" | "short-term-lease";
  readonly input: {
    readonly parcel: Parcel;
    readonly prices: MarketPriceSet;
    readonly items: BuildingItem[];
    readonly landTermYears: number;
    readonly temporaryUserType: "普通" | "政府或政府有关部门";
  };
}

export interface DemoLeaseRentFile {
  readonly calculationType: "lease-rent";
  readonly input: {
    readonly parcel: Parcel;
    readonly prices: MarketPriceSet;
    readonly items: BuildingItem[];
  };
}

export interface DemoCapacityIncreaseFile {
  readonly calculationType: "capacity-increase";
  readonly input: ChangePremiumInput & {
    /** main industrial land type of the after-parcel. */
    readonly mainIndustrialLandType: "M1" | "M0" | "W1" | "W0";
  };
}

/**
 * One case inside a batch. The `title` is optional — when absent the
 * runner falls back to `case-<index>` and the project name.
 */
export interface DemoBatchItem {
  readonly title?: string;
  readonly file: DemoNewSupplyFile | DemoChangePremiumFile;
}

export interface DemoBatchFile {
  readonly calculationType: "batch";
  readonly items: DemoBatchItem[];
}

export type DemoFile =
  | DemoNewSupplyFile
  | DemoChangePremiumFile
  | DemoExcessGfaFile
  | DemoTemporaryUseFile
  | DemoLeaseRentFile
  | DemoCapacityIncreaseFile
  | DemoBatchFile;

export class DemoInputError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "DemoInputError";
  }
}

export function parseDemoJson(raw: string, source: string = "<json>"): DemoFile {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    const reason = err instanceof Error ? err.message : String(err);
    throw new DemoInputError(`无法解析 JSON (${source}): ${reason}`);
  }
  const result = validateDemoFile(parsed);

  // For batch inputs, per-item errors live on `itemResults` rather
  // than the top-level `errors` array — collect them so the caller
  // sees a single error listing.
  //
  // Only `error` and `warning` levels block the input. `info` is
  // advisory (e.g. non-ISO date format) and is intentionally
  // non-blocking so users can record irregular values.
  const blocking = (m: ValidationMessage) =>
    m.level === "error" || m.level === "warning";
  const allErrors: ValidationMessage[] = result.errors.filter(blocking);
  const maybeBatch = result as unknown as {
    itemResults?: { errors: ValidationMessage[] }[];
  };
  if (Array.isArray(maybeBatch.itemResults)) {
    for (const ir of maybeBatch.itemResults) {
      allErrors.push(...ir.errors.filter(blocking));
    }
  }

  if (allErrors.length > 0) {
    throw new DemoInputError(formatErrors(source, allErrors));
  }
  if (result.value === null) {
    // Should not happen when errors is empty, but guard anyway.
    throw new DemoInputError(`${source}: 验证失败但未返回错误。`);
  }
  return result.value;
}

function formatErrors(source: string, errors: ValidationMessage[]): string {
  const head = `${source}: 共 ${errors.length} 处错误：`;
  const body = errors
    .map((e, i) => `  ${i + 1}. [${e.field ?? "<root>"}] ${e.message}`)
    .join("\n");
  return `${head}\n${body}`;
}